import { FC, useCallback } from 'react';
import { Calculator } from 'lucide-react';
import { funnelsAPI } from 'services/swagger-api';
import { useFunnelDetail } from '../FunnelDetailContext';
import { useAsyncResource } from 'back-office/shared/hooks/useAsyncResource';
import { BlockCard } from 'back-office/shared/components/BlockCard';
import { PricingGroup, PricingLine } from '../components/PricingLines';
import { DASH, formatAmount } from '../funnelFormat';

/**
 * Bloc « Tarification », colonne de droite, comme dans l'ancien écran.
 *
 * Un seul appel (`details-tarif-optim`) alimente tout le bloc, là où l'ancienne version
 * enchaînait prime pure totale, prix client, prix TTC et total des commissions — dont la
 * plupart des routes n'existent plus.
 */
export const PricingBlock: FC = () => {
  const { tunnelId, pricingVersion } = useFunnelDetail();

  // pricingVersion fait partie des dépendances à dessein : le changer relance le calcul
  // après toute mutation de garantie, de frais ou de configuration parc.
  const fetchPricing = useCallback(
    () => funnelsAPI.getFunnelPricingDetails({ tunnelId }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tunnelId, pricingVersion]
  );
  const { data, isLoading, error } = useAsyncResource(fetchPricing);

  const totals = data?.totaux;

  return (
    <BlockCard
      title="Tarification"
      description="Montants exprimés pour une période annuelle"
      icon={<Calculator className="h-4 w-4" />}
      isLoading={isLoading}
      error={error}
      isEmpty={!totals}
      emptyLabel="Aucune tarification calculable pour ce tunnel"
    >
      <PricingGroup>
        <PricingLine label="Prime pure" value={formatAmount(totals?.primePure)} />
      </PricingGroup>

      <PricingGroup>
        <PricingLine label="Somme des majorations" value={formatAmount(totals?.majoration)} />
        <PricingLine label="Somme des minorations" value={formatAmount(totals?.minoration)} />
        <PricingLine label="Prime pure totale" value={formatAmount(totals?.primePureTotal)} />
      </PricingGroup>

      {/* Commissions, garantie par garantie : le libellé porte le nom de la garantie. */}
      {(data?.garanties ?? []).some((garantie) => (garantie.remunerations ?? []).length > 0) && (
        <PricingGroup>
          {(data?.garanties ?? []).flatMap((garantie) =>
            (garantie.remunerations ?? []).map((remuneration, index) => (
              <PricingLine
                key={`${garantie.garantieId}-${remuneration.remunerationId}-${index}`}
                label={remuneration.libelle || DASH}
                context={garantie.designation ?? undefined}
                value={formatAmount(remuneration.montant)}
              />
            ))
          )}
        </PricingGroup>
      )}

      <PricingGroup>
        <PricingLine
          label="Somme des chargements"
          value={formatAmount(totals?.sommeRemunerations)}
        />
        <PricingLine label="Prix HT" value={formatAmount(totals?.prixHT)} />
        <PricingLine label="Taxe" value={formatAmount(totals?.taxe)} />
        <PricingLine label="Prix TTC" value={formatAmount(totals?.prixTTC)} />
      </PricingGroup>

      {(data?.frais ?? []).length > 0 && (
        <PricingGroup>
          {(data?.frais ?? []).map((frais, index) => (
            <PricingLine
              key={`${frais.fraisId}-${index}`}
              label={frais.libelle || DASH}
              value={formatAmount(frais.montantTTC)}
            />
          ))}
        </PricingGroup>
      )}

      <PricingGroup>
        <PricingLine label="Prix client" value={formatAmount(totals?.prixClient)} strong />
      </PricingGroup>
    </BlockCard>
  );
};
